import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
  CircularProgress,
  Typography,
  Paper,
  // Divider, // Removed as sections are now collapsible
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  OutlinedInput,
  SelectChangeEvent,
  FormHelperText,
  Switch,
  FormControlLabel,
} from '@mui/material';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '../../../store';
import { Journalist, JournalistFormData } from '../../../types/journalist.types';
import { createJournalist, updateJournalist } from '../slices/journalistsSlice';
import CollapsibleSection from '../../../components/common/CollapsibleSection';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import LinkIcon from '@mui/icons-material/Link';
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver';

interface JournalistFormProps {
  open: boolean;
  onClose: () => void;
  journalist?: Journalist;
  isEdit?: boolean;
  publisherId?: string;
  // Optional message to show at the top of the form (e.g., upgrade notice)
  upgradeMessage?: string;
  // Optional callback invoked after successful save
  onSaved?: (journalist: Journalist) => void;
  // Optional initial name when creating a new journalist
  initialName?: string;
}

const initialFormData: JournalistFormData = {
  name: '',
  email: '',
  profileImageUrl: '',
  socialLinks: {
    twitter: '',
    linkedin: '',
  },
  specialties: [],
  publisherPK: '',
  // Voice defaults
  voiceId: '',
  useVoiceId: false,
  signedVoiceReplicaConsent: false,
  allowAICoHost: false,
};

const JournalistForm: React.FC<JournalistFormProps> = ({
  open,
  onClose,
  journalist,
  isEdit = false,
  publisherId,
  upgradeMessage,
  onSaved,
  initialName,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const [formData, setFormData] = useState<JournalistFormData>(initialFormData);
  const [errors, setErrors] = useState<Partial<Record<string, string>>>({});
  const [isLoading, setIsLoading] = useState(false);

  const commonSpecialties = [
    'local politics', 'community events', 'sports', 'business', 
    'technology', 'health', 'education', 'arts', 'crime', 
    'environment', 'food', 'entertainment'
  ];

  useEffect(() => {
    if (!open) return;
    if (isEdit && journalist) {
      setFormData({
        name: journalist.name,
        email: journalist.email || '',
        profileImageUrl: journalist.profileImageUrl || '',
        socialLinks: {
          twitter: journalist.socialLinks?.twitter || '',
          linkedin: journalist.socialLinks?.linkedin || '',
        },
        specialties: journalist.specialties || [],
        publisherPK: journalist.publisherPK || '',
        // Voice fields from existing journalist
        voiceId: journalist.voiceId || '',
        useVoiceId: Boolean(journalist.useVoiceId),
        signedVoiceReplicaConsent: Boolean(journalist.signedVoiceReplicaConsent),
        allowAICoHost: Boolean(journalist.allowAICoHost),
      });
    } else {
      const pk = publisherId ? `PUBLISHER#${publisherId}` : '';
      setFormData({ 
        ...initialFormData, 
        publisherPK: pk, 
        name: initialName || ''
      });
    }
  }, [open, isEdit, journalist, publisherId, initialName]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    
    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setFormData({
        ...formData,
        [parent]: {
          ...(formData[parent as keyof JournalistFormData] as Record<string, any>) || {},
          [child]: value,
        },
      });
    } else {
      setFormData({
        ...formData,
        [name]: value,
      });
    }
    
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: undefined,
      });
    }
  };

  const handleBooleanToggle = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: checked,
    }));
  };

  const handleSpecialtiesChange = (event: SelectChangeEvent<string[]>) => {
    const value = event.target.value;
    
    setFormData({
      ...formData,
      specialties: typeof value === 'string' ? value.split(',') : value,
    });
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<Record<string, string>> = {};
    
    if (!formData.name) newErrors['name'] = 'Journalist name is required';
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (formData.email && !emailRegex.test(formData.email)) {
      newErrors['email'] = 'Please enter a valid email address';
    }
    
    const urlRegex = /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([\w/ .-]*)*\/?$/;
    
    if (formData.profileImageUrl && !urlRegex.test(formData.profileImageUrl)) {
      newErrors['profileImageUrl'] = 'Please enter a valid URL';
    }
    
    if (formData.socialLinks.twitter && !urlRegex.test(formData.socialLinks.twitter)) {
      newErrors['socialLinks.twitter'] = 'Please enter a valid URL';
    }
    
    if (formData.socialLinks.linkedin && !urlRegex.test(formData.socialLinks.linkedin)) {
      newErrors['socialLinks.linkedin'] = 'Please enter a valid URL';
    }

    if (formData.useVoiceId && !formData.voiceId) {
      newErrors['voiceId'] = 'Provide a custom voice id or disable "Use custom voice"';
    }

    if (!formData.publisherPK) newErrors['publisherPK'] = 'Missing publisher context';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    setIsLoading(true);
    try {
      if (isEdit && journalist) {
        const result = await dispatch(updateJournalist({ id: journalist.id, data: formData }));
        const payload: any = (result as any).payload;
        if (payload && onSaved) onSaved(payload as Journalist);
      } else {
        const result = await dispatch(createJournalist(formData));
        const payload: any = (result as any).payload;
        if (payload && onSaved) onSaved(payload as Journalist);
      }
      onClose();
    } catch (error) {
      console.error('Error saving journalist:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Collapsed summaries
  const basicInfoSummary = [formData.name, formData.email].filter(Boolean).join(' • ') || 'No info yet';
  const socialLinksSummary = [formData.socialLinks.twitter, formData.socialLinks.linkedin].filter(Boolean).join(' • ') || 'No links set';
  const voiceSummary = [
    formData.useVoiceId ? `Custom Voice: ${formData.voiceId || 'not set'}` : 'Custom Voice: off',
    formData.signedVoiceReplicaConsent ? 'Consent: signed' : 'Consent: not signed',
    formData.allowAICoHost ? 'AI Co-host: on' : 'AI Co-host: off',
  ].join(' • ');

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>{isEdit ? 'Edit Journalist' : 'Add New Journalist'}</DialogTitle>
      <DialogContent>
        {/* Optional upgrade message */}
        {upgradeMessage && (
          <Paper elevation={0} sx={{ p: 2, mb: 2, bgcolor: 'warning.light' }}>
            <Typography variant="body2" color="text.primary">
              {upgradeMessage}
            </Typography>
          </Paper>
        )}
        <Box component="form" noValidate sx={{ mt: 2 }}>
          <CollapsibleSection
            title="Basic Information"
            icon={<PersonOutlineIcon sx={{ mr: 1 }} />}
            namespace="journalist-form"
            id="basic-info"
            collapsedSummary={basicInfoSummary}
            paperProps={{ sx: { bgcolor: 'background.paper' } }}
          >
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Enter the journalist's basic information.
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Publisher"
                  value={publisherId ? publisherId : (formData.publisherPK || '').replace('PUBLISHER#','')}
                  InputProps={{ readOnly: true }}
                  helperText={isEdit ? 'Publisher cannot be changed' : 'Will be set to the current publisher'}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  required
                  label="Full Name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  error={!!errors['name']}
                  helperText={errors['name']}
                  disabled={isLoading}
                  placeholder="e.g., Jane Smith"
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Email Address"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  error={!!errors['email']}
                  helperText={errors['email']}
                  disabled={isLoading}
                  placeholder="e.g., jane.smith@example.com"
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Profile Image URL"
                  name="profileImageUrl"
                  value={formData.profileImageUrl}
                  onChange={handleInputChange}
                  error={!!errors['profileImageUrl']}
                  helperText={errors['profileImageUrl']}
                  disabled={isLoading}
                  placeholder="e.g., https://s3.amazonaws.com/location-station/profiles/janesmith.jpg"
                />
              </Grid>
              
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel id="specialties-label">Specialties</InputLabel>
                  <Select
                    labelId="specialties-label"
                    id="specialties"
                    multiple
                    value={formData.specialties}
                    onChange={handleSpecialtiesChange}
                    input={<OutlinedInput id="select-multiple-chip" label="Specialties" />}
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((value) => (
                          <Chip key={value} label={value} />
                        ))}
                      </Box>
                    )}
                  >
                    {commonSpecialties.map((specialty) => (
                      <MenuItem key={specialty} value={specialty}>
                        {specialty}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>
                    Select the journalist's areas of expertise
                  </FormHelperText>
                </FormControl>
              </Grid>
            </Grid>
          </CollapsibleSection>

          <Box sx={{ mt: '25px' }}>
            <CollapsibleSection
              title="Voice"
              icon={<RecordVoiceOverIcon sx={{ mr: 1 }} />}
              namespace="journalist-form"
              id="voice"
              collapsedSummary={voiceSummary}
              paperProps={{ sx: { bgcolor: 'background.paper' } }}
            >
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Configure how this journalist's articles are read on audio features.
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.useVoiceId}
                        onChange={handleBooleanToggle}
                        name="useVoiceId"
                        color="primary"
                        disabled={isLoading}
                      />
                    }
                    label="Use custom voice"
                  />
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: -1, mb: 1.5 }}>
                    Enable this to use a specific custom voice for this journalist's articles.
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <TextField
                      fullWidth
                      label="Custom Voice ID"
                      name="voiceId"
                      value={formData.voiceId}
                      onChange={handleInputChange}
                      error={!!errors['voiceId']}
                      helperText={errors['voiceId'] || 'The unique ID of the custom voice model to use for this journalist.'}
                      disabled={isLoading || !formData.useVoiceId}
                      placeholder="e.g., voice_abc123"
                    />
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.signedVoiceReplicaConsent}
                        onChange={handleBooleanToggle}
                        name="signedVoiceReplicaConsent"
                        color="primary"
                        disabled={isLoading}
                      />
                    }
                    label="Signed Voice Replica Consent"
                  />
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: -1, mb: 1.5 }}>
                    Indicates whether the journalist has signed the Voice Replica Consent form.
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.allowAICoHost}
                        onChange={handleBooleanToggle}
                        name="allowAICoHost"
                        color="primary"
                        disabled={isLoading}
                      />
                    }
                    label="Allow AI Co-host"
                  />
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: -1 }}>
                    If enabled, an AI co-host may accompany the journalist. When disabled, articles are read with a solo host style.
                  </Typography>
                </Grid>
              </Grid>
            </CollapsibleSection>
          </Box>
          
          <Box sx={{ mt: '25px' }}>
            <CollapsibleSection
              title="Social Media Links"
              icon={<LinkIcon sx={{ mr: 1 }} />}
              namespace="journalist-form"
              id="social-links"
              collapsedSummary={socialLinksSummary}
              paperProps={{ sx: { bgcolor: 'background.paper' } }}
            >
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Twitter Profile"
                    name="socialLinks.twitter"
                    value={formData.socialLinks.twitter}
                    onChange={handleInputChange}
                    error={!!errors['socialLinks.twitter']}
                    helperText={errors['socialLinks.twitter']}
                    disabled={isLoading}
                    placeholder="e.g., https://twitter.com/janesmith"
                  />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="LinkedIn Profile"
                    name="socialLinks.linkedin"
                    value={formData.socialLinks.linkedin}
                    onChange={handleInputChange}
                    error={!!errors['socialLinks.linkedin']}
                    helperText={errors['socialLinks.linkedin']}
                    disabled={isLoading}
                    placeholder="e.g., https://linkedin.com/in/janesmith"
                  />
                </Grid>
              </Grid>
            </CollapsibleSection>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={isLoading}
          startIcon={isLoading ? <CircularProgress size={20} /> : null}
        >
          {isEdit ? 'Update' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default JournalistForm;